import React, { useCallback, useContext } from 'react'
import PropTypes from 'prop-types'
import { Box, Flex, LinkBox, ListItem, Text } from '@chakra-ui/react'
import { BulletIcon } from './BulletIcon'
import { VerticalLine } from './VerticalLine'

import { isActive, scrollToReport } from '../../../utils/MenuHelper'
import { observer } from 'mobx-react'
import rootStore from '../../../store/RootStore'
import { ReportContext } from '../../../contexts/ReportContext'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

export const MenuItem = observer(
  ({ title, id: itemId, items, type, indent = 0, lastItem = false }) => {
    const {
      reportStore: { currentChartId, currentSectionId },
    } = rootStore

    const { refScrollContainer } = useContext(ReportContext)
    const location = useLocation()
    const navigate = useNavigate()
    const { reportId } = useParams()

    const handleSelectMenuItem = useCallback(() => {
      if (location.pathname !== '/reports') {
        navigate(`/reports/${reportId}${location.search}`, {
          state: { chartId: itemId },
        })
      }
      scrollToReport(itemId, refScrollContainer?.current)
      // Let usePageScroll handle setting currentSectionId based on actual scroll position
    }, [
      reportId,
      itemId,
      location.pathname,
      location.search,
      navigate,
      refScrollContainer,
    ])

    const isTable = type === 'table'

    const isItemActive = isActive(currentSectionId, { id: itemId, children: items || [] }) || itemId === currentChartId

    return (
      <ListItem
        color={isItemActive ? 'white' : 'whiteAlpha.500'}
        position={'relative'}
        _hover={{
          color: 'white',
        }}>
        {isTable && <Separator />}
        <LinkBox
          onClick={handleSelectMenuItem}
          cursor="pointer">
          <Flex
            alignItems="center"
            zIndex={1}
            position="relative"
            width="100%">
            {!isTable && (
              <ItemBulletIcon
                lastItem={lastItem}
                indent={indent}
                chartId={itemId}
                items={items}
              />
            )}
            <Text
              textStyle={`menuHeader${indent + 1}`}
              whiteSpace={'nowrap'}
              mb="12px"
              {...getTextProps(isTable)}>
              {title}
            </Text>
          </Flex>
        </LinkBox>
      </ListItem>
    )
  },
)

const getTextProps = isTable =>
  !isTable
    ? {
      mt: 0,
      pt: 0,
      pl: 4,
    }
    : {
      mt: 1,
      pt: 2,
      pl: 4,
    }

MenuItem.propTypes = {
  title: PropTypes.string,
  id: PropTypes.string,
  items: PropTypes.array,
  type: PropTypes.string,
  indent: PropTypes.number,
  lastItem: PropTypes.bool,
  chartId: PropTypes.string,
}

const ItemBulletIcon = observer(({ chartId, items, indent, lastItem }) => {
  const {
    reportStore: { currentChartId, currentSectionId },
  } = rootStore
  // Check if this item is active using hierarchical checking
  const isItemActive = isActive(currentSectionId, { id: chartId, children: items || [] }) ||
    chartId === currentChartId
  return (
    <>
      {!lastItem ? <VerticalLine indent={indent} /> : null}
      <BulletIcon
        active={isItemActive}
        indent={indent}
      />
    </>
  )
})

const Separator = () => (
  <Box
    position="absolute"
    zIndex={0}
    top={0}
    left={-4}>
    <svg
      width={'100%'}
      height={2}>
      <line
        x1={0}
        y1={0}
        x2={999}
        y2={0}
        stroke="rgba(255, 255, 255, .1)"
      />
    </svg>
  </Box>
)
